/**
 * N-03 «Включить уведомления» и N-04 «Уведомления запрещены».
 *
 * ПС §18 «Правило разрешений»: системный запрос идёт ТОЛЬКО после отдельного
 * объяснения и отдельного действия «Включить». Автоматический запрос при
 * загрузке запрещён — часть браузеров его и не покажет.
 *
 * ПС §12: после отказа приложение продолжает работать, а непрочитанное
 * по-прежнему видно внутри интерфейса (счётчики и центр уведомлений).
 */
import { useCallback } from 'react'

import { usePushPermission } from '@features/push/use-push'
import { launchMode } from '@shared/pwa/platform'
import { Banner, Button, Dialog, Icon, useToast } from '@shared/ui'

import { useAppStore } from '../../model/app-store'
import styles from './push.module.css'

/* ===================================================================== */
/* N-03 — модальное окно                                                  */
/* ===================================================================== */

/**
 * Общий обработчик «Включить»: системный запрос по жесту, тост по исходу.
 * `onDone` закрывает ту форму предложения, из которой нажали.
 */
function usePushEnable(onDone: () => void): { enable: () => Promise<void>; busy: boolean } {
  const config = useAppStore((s) => s.config)
  const toast = useToast()
  const push = usePushPermission(config)

  const enable = useCallback(async () => {
    const outcome = await push.enable()
    onDone()

    switch (outcome) {
      case 'granted':
      case 'granted_without_subscription':
        toast.show('Уведомления включены')
        break
      case 'denied':
        // N-04: объяснение остаётся на N-01 и S-04, повторный запрос невозможен.
        toast.show('Уведомления запрещены в браузере', { tone: 'danger' })
        break
      case 'failed':
        toast.show('Не удалось подключить уведомления. Попробуйте позже.', { tone: 'danger' })
        break
      default:
        break
    }
  }, [onDone, push, toast])

  return { enable, busy: push.busy }
}

/**
 * Полоса-предложение для автоматических точек входа (первый match, первый
 * открытый чат) — как «Включить уведомления» над списком в Telegram Web.
 * Не перекрывает экран; в открытом чате не показывается (AppShell).
 */
export function PushOfferBar(): React.ReactElement | null {
  const open = useAppStore((s) => s.pushOfferOpen)
  const closePushOffer = useAppStore((s) => s.closePushOffer)
  const { enable, busy } = usePushEnable(closePushOffer)

  if (!open) return null

  return <OfferBar onEnable={() => void enable()} busy={busy} onDismiss={closePushOffer} />
}

/** Тонкая полоса «Включить уведомления»: значок, две строки, кнопка и — если есть куда — крестик. */
function OfferBar({
  onEnable,
  busy = false,
  onDismiss,
}: {
  onEnable: () => void
  busy?: boolean
  onDismiss?: () => void
}): React.ReactElement {
  return (
    <div className={styles['offer']} role="region" aria-label="Уведомления">
      <span className={styles['offerIcon']} aria-hidden>
        <Icon name="bell" size={20} />
      </span>
      <div className={styles['offerText']}>
        <span className={styles['offerTitle']}>Включить уведомления</span>
        <span className={styles['offerHint']}>О совпадениях и сообщениях, даже когда приложение закрыто</span>
      </div>
      <Button size="sm" onClick={onEnable} loading={busy}>
        Включить
      </Button>
      {onDismiss ? (
        <button type="button" className={styles['offerClose']} aria-label="Не сейчас" onClick={onDismiss}>
          <Icon name="close" size={18} />
        </button>
      ) : null}
    </div>
  )
}

/**
 * Модальное объяснение — для явных действий пользователя: «Включить» в S-04
 * и N-01, первый запуск установленного Web App (§8.1 п. 2).
 */
export function PushPromptHost(): React.ReactElement | null {
  const open = useAppStore((s) => s.pushPromptOpen)
  const closePushPrompt = useAppStore((s) => s.closePushPrompt)
  const { enable, busy } = usePushEnable(closePushPrompt)

  if (!open) return null

  return (
    <Dialog
      open
      onClose={closePushPrompt}
      title="Включить уведомления"
      description="Чтобы не пропустить новое совпадение и сообщение, когда приложение закрыто."
      footer={
        <>
          <Button variant="secondary" onClick={closePushPrompt}>
            Не сейчас
          </Button>
          <Button onClick={() => void enable()} loading={busy}>
            Включить
          </Button>
        </>
      }
    >
      <ul className={styles['benefits']}>
        <li>
          <Icon name="heart" size={18} />
          <span>Новое совпадение — сразу, а не при следующем заходе</span>
        </li>
        <li>
          <Icon name="chats" size={18} />
          <span>Сообщения от тех, с кем уже общаетесь</span>
        </li>
        <li>
          <Icon name="shield" size={18} />
          <span>Важное по аккаунту: вход с нового устройства, решения модерации</span>
        </li>
      </ul>

      <p className={styles['note']}>
        В самом уведомлении нет ни текста сообщения, ни имени — они загружаются, только когда вы его
        открываете. Превью можно отключить в настройках.
      </p>
    </Dialog>
  )
}

/* ===================================================================== */
/* N-04 — баннер состояния                                                */
/* ===================================================================== */

/**
 * Статус разрешения там, где он уместен: N-01 и S-04.
 * Системный запрос при `denied` не повторяется — только инструкция.
 *
 * Формулировки для iPhone — по CLIENT_IOS_WEBAPP.md §5.2: «запрещены в
 * настройках iOS», «недоступны в этой версии iOS», «доступны после установки
 * на экран „Домой“» — во вкладке Safari Web Push нет (IOS §10).
 */
export function PushStatusBanner({ compact = false }: { compact?: boolean }): React.ReactElement | null {
  const config = useAppStore((s) => s.config)
  const openPushPrompt = useAppStore((s) => s.openPushPrompt)
  const push = usePushPermission(config)
  const mode = launchMode()

  switch (push.state) {
    // §8.1 п. 4: сервер отключил канал — включать нечего, предложение не показываем.
    case 'server_disabled':
    case 'granted':
      return null

    case 'unsupported':
      if (compact) return null

      if (mode === 'safari_tab') {
        return (
          <Banner tone="info" icon="bell" title="Уведомления доступны после установки на экран «Домой»">
            Добавьте «Движ» на экран «Домой» — пункт «Установить приложение» в настройках. До этого новое
            видно на вкладках и в центре уведомлений.
          </Banner>
        )
      }

      if (mode === 'installed_web_app') {
        return (
          <Banner tone="neutral" icon="info" title="Уведомления недоступны в этой версии iOS">
            Они появятся после обновления iOS до 16.4 или новее. Непрочитанное по-прежнему видно на вкладках и
            в центре уведомлений.
          </Banner>
        )
      }

      return (
        <Banner tone="neutral" icon="info" title="Уведомления недоступны в этом браузере">
          Новое по-прежнему видно внутри приложения: счётчики на вкладках и центр уведомлений работают.
        </Banner>
      )

    case 'denied':
      return (
        <Banner tone="warning" icon="bell" title="Уведомления запрещены">
          {mode === 'installed_web_app'
            ? 'Разрешение выдаётся в настройках iOS: «Настройки» → «Уведомления» → «Движ».'
            : 'Разрешение выдаётся в настройках браузера: значок слева от адреса → «Уведомления» → «Разрешить».'}{' '}
          Непрочитанное всё это время видно на вкладках и в центре уведомлений.
        </Banner>
      )

    case 'prompt':
      // В служебном диалоге — та же тонкая полоса, что над списком чатов; «Включить»
      // ведёт в модальное объяснение (явное действие, ПС §18).
      if (compact) return <OfferBar onEnable={openPushPrompt} />

      return (
        <Banner
          tone="info"
          icon="bell"
          title="Уведомления выключены"
          actions={
            <Button size="sm" onClick={openPushPrompt}>
              Включить
            </Button>
          }
        >
          Сообщения и совпадения будут приходить, даже когда вкладка закрыта.
        </Banner>
      )

    default:
      return null
  }
}
